import { LocalDate } from '@js-joda/core';
import { i18n } from 'i18n';
import useScenario from 'modules/project/providers/scenario/use';
import { batchBySize } from 'utils';
import { useSnackbar } from 'notistack';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient
} from 'react-query';
import {
  AddNodeBetweenNodesForm,
  ApplySpoolStatusFromDatesResultDto,
  BulkImportStopesForm,
  BulkImportStopesResultDto,
  BulkUpdateStopeGeometryForm,
  DufBulkImportParseResponse,
  DxfImportDataForm,
  FileParameter,
  FileResponse,
  InspectionPointDto,
  MineModelDto,
  MineModelSummaryDto,
  ModelNodeDataForm,
  NodeVolumeRecordDto,
  PagedListOfInspectionPointDto,
  PagedListOfWearDataDto,
  PourShapesExportDto,

  ReticulationDataImportDto,
  SpoolImportDto,
  SpoolUpdateForm,
  RheologyDataDto,
  RheologyDataForm,
  RemoveSpoolsForm,
  RemoveStopesForm,
  RemoveStopesResultDto,
  StopeDataForm,
  TunnelProfileDto,
  TunnelProfileSettingsForm,
  UpdateModelNodeDataForm,
  UpdateStopeDataForm,
  UpdateWearDataForm,
  WearDataDto,
  WearDataForm,
  ModelEntityGroupDto,
  CreateModelEntityGroupForm,
  UpdateModelEntityGroupForm
} from './api';
import useApi, {
  makeCreateMutation,
  makeDeleteMutation,
  makeGetQuery,
  makeListQuery,
  makeUpdateMutation,
  PagedListOfT
} from './useApi';

import { enqueueReticulationWrite, serialiseReticulationWrites } from './reticulationWriteQueue';
import { STOPE_BACKFILLS_KEY } from './usePours';
import { RECIPES_KEY } from './useRecipes';

export const MINE_MODEL_KEY = 'mine-model';
export const MINE_MODEL_SUMMARY_KEY = 'mine-model-summary';
const RHEOLOGY_DATA_EXPORT_KEY = 'rheology-data-export';
export const RETICULATION_DATA_EXPORT_KEY = 'reticulation-data-export';
const INSPECTION_POINTS_KEYS = 'inspection-points';
const INSPECTION_POINTS_KEY = 'inspection-point';
const WEAR_DATA_LIST_KEY = 'wear-data-list';
const WEAR_DATA_KEY = 'wear-data';
const NODE_VOLUME_RECORD_KEY = 'node-volume-record';

// Mine Model
export interface MineModelMutation {
  projectId: string;
  projectName: string;
  data: ReticulationDataImportDto;
  // Undo/restore sets this to bypass the "stope has pours planned" guard when reverting reticulation.
  force?: boolean;
}
export interface MineModelQueryParams {
  mineModelId: string;
  scenarioId?: string;
}

export const useMineModelQuery = makeGetQuery<MineModelDto | undefined, MineModelQueryParams>(
  MINE_MODEL_KEY,
  (api, params) => () => api.mineModels.getWithRheologyList(params.mineModelId, params.scenarioId).catch(() => undefined),
);

export const useMineModelSummaryQuery = makeGetQuery<MineModelSummaryDto | undefined, MineModelQueryParams>(
  MINE_MODEL_SUMMARY_KEY,
  (api, params) => () => api.mineModels.getSummary(params.mineModelId, params.scenarioId).catch(() => undefined),
);

export const useCreateMineModelMutation = makeCreateMutation<void, MineModelMutation>(
  null,
  serialiseReticulationWrites((api) => (mutation) => api.mineModels.create(mutation.projectId, mutation.data)),
  () => i18n._('Reticulation data added'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useUpdateMineModelMutation = makeUpdateMutation<void, MineModelMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.update(projectId, mutation.data, scenarioId, mutation.force)),
  () => i18n._('Reticulation data updated'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

// Rheology CRUD
export interface MineModelRheologyDataQuery {
  mineModelId: string;
  rheologyDataSetIds: string[];
}
export const useMineModelRheologyDataListQuery = makeGetQuery<RheologyDataDto[], MineModelRheologyDataQuery>(
  MINE_MODEL_KEY,
  (api, query) => async () => {
    try {
      return await api.mineModels.getRheologyDataByListOfIds(query.mineModelId, { rheologyDataSetIds: query.rheologyDataSetIds });
    } catch (error: any) {
      if (error?.status === 406) {
        return [];
      }
      throw error;
    }
  },
);

export interface RheologyMutation {
  projectId: string;
  projectName: string;
  data: RheologyDataForm;
  rheologyDatasetId?: string;
}

export interface BulkRheologyMutation {
  projectId: string;
  projectName: string;
  rheologyParameters?: Array<{
    name: string;
    yieldStressA: number;
    yieldStressB: number;
    viscosityC: number;
    viscosityD: number;
    solidsDensity: number;
  }>;
  globalParameters?: {
    factorOfSafety: number;
    maxPumpPressure: number;
    defaultPumpPressure: number;
    flowRateMin: number;
    flowRateMax: number;
    flowRateInterval: number;
    flowRateDefault: number;
    massConcentrationMin: number;
    massConcentrationMax: number;
    massConcentrationInterval: number;
    binderMin: number;
    binderMax: number;
    binderInterval: number;
    carrierFluidDensity: number;
    binderDensity: number;
  };
  ucsParameters?: Array<{
    days: number;
    ucs1: number;
    ucs2: number;
  }>;
  pipes?: Array<{
    id: string;
    name: string;
    calculationMode: string;
    isMetric: boolean;
    dimensionStandard: string;
    pipeSize: string;
    wallSelection: string;
    insideDiameter: number;
    outsideDiameter: number;
    wallThickness: number;
    liningThickness: number;
    wearAllowance: number;
    materialStandard: string;
    materialGrade: string;
    pressureRatingCode: string;
    factorOfSafety: number;
    weldType: string;
    couplingStandard: string;
    couplingClass: string;
    cutDepth: number;
    designTemperature: number;
    pipeRoughness: number;
    simplePressureRating?: number;
    pipePressureRating: number;
    couplingPressureRating: number;
    overallPressureRating: number;
    colour?: string;
    startingThickness?: number;
    minimumThickness?: number;
  }>;
}

export const useCreateRheologyMutation = makeUpdateMutation<void, RheologyMutation>(
  MINE_MODEL_KEY, null,
  (api) => (mutation) => api.mineModels.createRheologyData(mutation.projectId, mutation.data),
  () => i18n._('Rheology data added'),
  // MINE_MODEL_KEY (prefix) so the object-keyed useMineModelQuery(['mine-model', { mineModelId }]) refetches
  // — otherwise the rheology list only updates after a manual refresh.
  [MINE_MODEL_KEY, RHEOLOGY_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useUpdateRheologyMutation = makeUpdateMutation<void, RheologyMutation>(
  MINE_MODEL_KEY, null,
  (api, projectId) => (mutation) => api.mineModels.updateRheology(projectId, mutation.data, mutation.rheologyDatasetId),
  () => i18n._('Rheology data updated'),
  [MINE_MODEL_KEY, RHEOLOGY_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useSilentUpdateRheologyMutation = makeUpdateMutation<void, RheologyMutation>(
  MINE_MODEL_KEY, null,
  (api, projectId) => (mutation) => api.mineModels.updateRheology(projectId, mutation.data, mutation.rheologyDatasetId),
  () => '',
  [RHEOLOGY_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
  false,
);

export const useBulkRheologyMutation = makeUpdateMutation<void, BulkRheologyMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId) => (mutation) => api.mineModels.bulkRheologyData(projectId, mutation)),
  () => i18n._('Rheology data saved'),
  [MINE_MODEL_KEY, RHEOLOGY_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface SingleRheologyDataSetArgs {
  mineModelId: string;
  rheologyDatasetId: string;
  rheologyDatasetReference?: string;
}

export const useDeleteRheologyDatasetMutation = makeDeleteMutation<void, SingleRheologyDataSetArgs>(
  MINE_MODEL_KEY, null,
  (api) => (mutation) => api.mineModels.removeRheologyDataSet(mutation.mineModelId, mutation.rheologyDatasetId),
  (mutation) => i18n._('Rheology data').concat(` ${mutation.rheologyDatasetReference} `).concat(i18n._('successfully deleted')),
  (mutation) => mutation.rheologyDatasetId,
  [MINE_MODEL_KEY, RHEOLOGY_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
  // The backend rejects deleting a dataset referenced by pours or recipes (mirrors the
  // stope/node "pours planned" guard) - deactivation is the supported path for those.
  i18n._('Cannot delete a rheology dataset which is used by pours or recipes. Deactivate it instead.'),
);

export interface SetRheologyDeactivatedMutation {
  mineModelId: string;
  rheologyDatasetId: string;
  isDeactivated: boolean;
}

// Soft archive: deactivated datasets are hidden from new-recipe selection but kept, so pours and
// QAQC data that reference them keep working (unlike delete, which breaks those references).
export const useSetRheologyDeactivatedMutation = makeUpdateMutation<void, SetRheologyDeactivatedMutation>(
  MINE_MODEL_KEY, null,
  (api) => (mutation) => api.mineModels.setRheologyDataDeactivated(mutation.mineModelId, {
    rheologyDataSetId: mutation.rheologyDatasetId,
    isDeactivated: mutation.isDeactivated,
  }),
  (mutation) => (mutation.isDeactivated ? i18n._('Rheology data deactivated') : i18n._('Rheology data reactivated')),
  [MINE_MODEL_KEY, RHEOLOGY_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useRheologyDataExport = makeGetQuery<RheologyDataDto | undefined, SingleRheologyDataSetArgs>(
  RHEOLOGY_DATA_EXPORT_KEY,
  (api, request) => () => api.mineModels.exportRheologyData(request.mineModelId, request.rheologyDatasetId).catch(() => undefined),
);

export const useRheologyDataQuery = makeGetQuery<RheologyDataDto | undefined, SingleRheologyDataSetArgs>(
  RHEOLOGY_DATA_EXPORT_KEY,
  (api, request) => () => api.mineModels.getRheologyDataSet(request.mineModelId, request.rheologyDatasetId).catch(() => undefined),
);

// Reticulation CRUD

export interface ReticulationDataExportParams {
  mineModelId: string;
  scenarioId?: string;
}

export const useReticulationDataExport = makeGetQuery<ReticulationDataImportDto | undefined, ReticulationDataExportParams>(
  RETICULATION_DATA_EXPORT_KEY,
  (api, { mineModelId, scenarioId }) => () => api.mineModels.exportReticulationData(mineModelId, scenarioId).catch(() => undefined),
);

export const useExportReticulationNodesCsvMutation = makeUpdateMutation<FileResponse, string>(
  MINE_MODEL_KEY,
  null,
  (api, _entityId, scenarioId) => (mineModelId) => api.mineModels.exportReticulationNodesCsv(mineModelId, scenarioId),
  () => '',
  [],
  false,
);

export const useExportReticulationDataDufMutation = makeUpdateMutation<FileResponse, string>(
  MINE_MODEL_KEY,
  null,
  (api, _entityId, scenarioId) => (mineModelId) => api.mineModels.exportReticulationDataDuf(mineModelId, scenarioId),
  () => '',
  [],
  false,
);

export interface ExportPourShapesDufMutation {
  mineModelId: string;
  stopeId: string;
  exportData: PourShapesExportDto;
}

export const useExportPourShapesDufMutation = makeUpdateMutation<FileResponse, ExportPourShapesDufMutation>(
  MINE_MODEL_KEY,
  null,
  (api) => (mutation) => api.mineModels.exportPourShapesDuf(mutation.mineModelId, mutation.stopeId, mutation.exportData),
  () => '',
  [],
  false,
);

export interface BulkUpdateNodesCsvMutation {
  fileParam: FileParameter;
}

export const useBulkUpdateNodesCsvMutation = makeUpdateMutation<string, BulkUpdateNodesCsvMutation>(
  MINE_MODEL_KEY,
  null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.bulkUpdateNodesFromCsv(projectId, scenarioId, mutation.fileParam)),
  () => i18n._('Nodes updated'),
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY, MINE_MODEL_KEY],
);

export interface StopesMutation {
  projectName: string;
  data: UpdateStopeDataForm[];
}

export interface SingleStopeMutation {
  projectName: string | null;
  nodeId: string;
  data: StopeDataForm;
}

export interface DocumentMutation {
  stopeId: string;
  files: FileParameter[];
}

export interface RemoveDocumentMutation {
  stopeId: string;
  fileName: string;
}

export const useUpdateStopeMutation = makeUpdateMutation<void, StopesMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.updateStopes(projectId, mutation.data, scenarioId)),
  () => i18n._('Stope data updated'),
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY, MINE_MODEL_KEY],
);

// Same call, no snackbar and no refresh - for a chain of reticulation saves that refreshes once after the last.
export const useUpdateStopeSilentMutation = makeUpdateMutation<void, StopesMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.updateStopes(projectId, mutation.data, scenarioId)),
  () => '',
  [],
  false,
);

export const useAddDocumentsToStopeMutation = makeUpdateMutation<FileResponse, DocumentMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.addDocument(projectId, mutation.stopeId, scenarioId, mutation.files)),
  () => i18n._('Documents added'),
  // MINE_MODEL_KEY (prefix) so the object-keyed useMineModelQuery(['mine-model', { mineModelId }]) refetches
  // — otherwise the stope's documents only update after a manual refresh.
  [MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useRemoveDocumentsFromStopeMutation = makeUpdateMutation<void, RemoveDocumentMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.removeDocument(projectId, mutation.stopeId, mutation.fileName, scenarioId)),
  () => i18n._('Document removed'),
  [MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useUpdateNodeToStopeMutation = makeUpdateMutation<string, SingleStopeMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.addStope(projectId, mutation.nodeId, mutation.data, scenarioId)),
  () => i18n._('Stope created'),
  [RETICULATION_DATA_EXPORT_KEY, STOPE_BACKFILLS_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface SingleNodeMutation {
  projectName: string | null;
  nodeId: string;
  data: UpdateModelNodeDataForm;
}

export interface MultipleNodeMutation {
  projectId: string;
  projectName: string | null;
  nodeId: string;
  data: ModelNodeDataForm[];
}

export interface DxfImportMutation {
  projectId: string;
  projectName: string | null;
  nodeId: string;
  data: DxfImportDataForm[];
}

export interface DeleteNodeMutation {
  projectId: string;
  projectName: string | null;
  nodeId: string[];
  data: ModelNodeDataForm[];
  forceDelete?: boolean;
}

export interface ResetSpoolThroughputMutation {
  projectId: string;
  spoolIds: string[];
}

export interface AddNodeBetweenNodesMutation {
  projectId: string;
  data: AddNodeBetweenNodesForm;
}

export const useUpdateNodeMutation = makeUpdateMutation<string, SingleNodeMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.updateNode(projectId, mutation.data, scenarioId)),
  () => i18n._('Node data updated'),
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY, MINE_MODEL_KEY],
);

export interface BulkNodeMutation {
  projectId: string;
  projectName: string | null;
  data: UpdateModelNodeDataForm[];
}

export const useBulkUpdateNodesMutation = makeUpdateMutation<string, BulkNodeMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.bulkUpdateNodes(projectId, mutation.data, scenarioId)),
  () => i18n._('Nodes updated'),
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY, MINE_MODEL_KEY],
);

export const useBulkUpdateNodesSilentMutation = makeUpdateMutation<string, BulkNodeMutation>(
  MINE_MODEL_KEY,
  null,
  serialiseReticulationWrites((api, entityId, scenarioId) => (mutation) => api.mineModels.bulkUpdateNodes(entityId, mutation.data, scenarioId)),
  () => '',
  [],
  false,
);

export const invalidateBulkNodeKeys = (queryClient: ReturnType<typeof useQueryClient>) => {
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY, MINE_MODEL_KEY].forEach(
    (key) => queryClient.invalidateQueries(key, { exact: false }),
  );
};

// cancelRefetch: a fetch already in flight may have read the model before this save, so it must not answer for it.
export const refreshMineModel = (queryClient: ReturnType<typeof useQueryClient>) => (
  queryClient.invalidateQueries(MINE_MODEL_KEY, { exact: false }, { cancelRefetch: true })
);

type ProjectMutationHook<TData, TModel> = (
  entityId: string,
  options?: UseMutationOptions<TData, unknown, TModel>,
) => UseMutationResult<TData, unknown, TModel>;

// makeUpdateMutation's own invalidation is keyed by a string id and misses the object-keyed mine model, so reticulation
// writes whose callers patch the scene in place wrap themselves in this: the stopes page re-initialises from the cached
// model on remount and records undo history from it, so the cache must follow every save.
const withMineModelRefresh = <TData, TModel>(useBaseMutation: ProjectMutationHook<TData, TModel>): ProjectMutationHook<TData, TModel> => (
  (entityId, options) => {
    const queryClient = useQueryClient();
    return useBaseMutation(entityId, {
      ...options,
      onSuccess: (...args) => {
        options?.onSuccess?.(...args);
        refreshMineModel(queryClient);
      },
    });
  }
);

// Spools own every edge in the reticulation graph, so connect/disconnect/change-pipe/change-flow all
// funnel through these three. Each is one request against the one-per-mine reticulation aggregate:
// batch into a single call rather than firing one per spool, or the saves collide (ConcurrencyException).
export interface AddSpoolsMutation {
  projectId: string;
  spools: SpoolImportDto[];
}

export const useAddSpoolsMutation = withMineModelRefresh(makeUpdateMutation<FileResponse, AddSpoolsMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.addSpools(projectId, mutation.spools, scenarioId)),
  () => i18n._('Nodes connected'),
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
));

export interface RemoveSpoolsMutation {
  projectId: string;
  form: RemoveSpoolsForm;
}

export const useRemoveSpoolsMutation = withMineModelRefresh(makeUpdateMutation<void, RemoveSpoolsMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.removeSpools(projectId, mutation.form, scenarioId)),
  () => i18n._('Connections removed'),
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
));

export interface BulkUpdateSpoolsMutation {
  projectId: string;
  spools: SpoolUpdateForm[];
}

export const useBulkUpdateSpoolsMutation = withMineModelRefresh(makeUpdateMutation<FileResponse, BulkUpdateSpoolsMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.bulkUpdateSpools(projectId, mutation.spools, scenarioId)),
  () => i18n._('Spools updated'),
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
));

// Same call, no snackbar and no model refresh - for debounced click-and-drag tools, which refresh once their queue drains.
export const useBulkUpdateSpoolsSilentMutation = makeUpdateMutation<FileResponse, BulkUpdateSpoolsMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.bulkUpdateSpools(projectId, mutation.spools, scenarioId)),
  () => '',
  [],
  false,
);

export interface ApplySpoolStatusFromDatesMutation {
  projectId: string;
  asOf: LocalDate;
}

export const useApplySpoolStatusFromDatesMutation = withMineModelRefresh(makeUpdateMutation<ApplySpoolStatusFromDatesResultDto, ApplySpoolStatusFromDatesMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.applySpoolStatusFromDates(projectId, { asOf: mutation.asOf }, scenarioId)),
  () => '',
  [RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
));

export const useAddNodeBetweenNodesMutation = makeCreateMutation<string, AddNodeBetweenNodesMutation>(
  MINE_MODEL_KEY,
  serialiseReticulationWrites((api, scenarioId) => (mutation) => api.mineModels.addNodeBetweenNodes(mutation.projectId ?? '', mutation.data, scenarioId)),
  () => i18n._('Node data updated'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useAddNodeMutation = makeCreateMutation<string, MultipleNodeMutation>(
  MINE_MODEL_KEY,
  serialiseReticulationWrites((api, scenarioId) => (mutation) => api.mineModels.addNode(mutation.projectId ?? '', mutation.data, scenarioId)),
  () => i18n._('Node data updated'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useDxfImportMutation = makeCreateMutation<string, DxfImportMutation>(
  MINE_MODEL_KEY,
  serialiseReticulationWrites((api, scenarioId) => (mutation) => api.mineModels.dxfImport(mutation.projectId ?? '', mutation.data, scenarioId)),
  () => i18n._('Node data updated'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const removeAddedNodeMutation = makeDeleteMutation<void, DeleteNodeMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, scenarioId) => (mutation) => api.mineModels.removeNode(mutation.projectId, mutation.nodeId, mutation.forceDelete, scenarioId)),
  (mutation) => i18n._('Node').concat(` ${mutation.nodeId} `).concat(i18n._('successfully deleted')),
  (mutation) => mutation.projectId,
  [RETICULATION_DATA_EXPORT_KEY, STOPE_BACKFILLS_KEY, MINE_MODEL_KEY, MINE_MODEL_SUMMARY_KEY],
  i18n._('Cannot remove a node which has pours planned.'),
);

export const useResetSpoolThroughputMutation = makeUpdateMutation<void, ResetSpoolThroughputMutation>(
  MINE_MODEL_KEY,
  null,
  serialiseReticulationWrites((api, projectId, scenarioId) => async (mutation) => {
    await api.mineModels.resetSpoolThroughput(projectId, { spoolIds: mutation.spoolIds }, scenarioId);
  }),
  () => i18n._('Spool throughput reset'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY, MINE_MODEL_KEY, NODE_VOLUME_RECORD_KEY],
);

export interface SingleStopeDeleteMutation {
  projectId: string;
  stopeId: string;
  stopeName: string;
  nodeId: string;
}

export const useDeleteStopeMutation = makeDeleteMutation<void, SingleStopeDeleteMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, scenarioId) => (mutation) => api.mineModels.removeStope(mutation.projectId, mutation.stopeId, mutation.nodeId, scenarioId)),
  (mutation) => i18n._('Stope').concat(` ${mutation.stopeName} `).concat(i18n._('successfully deleted')),
  (mutation) => mutation.projectId,
  [RETICULATION_DATA_EXPORT_KEY, STOPE_BACKFILLS_KEY, MINE_MODEL_KEY, MINE_MODEL_SUMMARY_KEY],
  i18n._('Cannot remove a stope which has pours planned.'),
);

export interface BulkDeleteStopesMutation {
  projectId: string;
  form: RemoveStopesForm;
}

export const useDeleteStopesMutation = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { activeScenarioId } = useScenario();

  return useMutation<RemoveStopesResultDto, unknown, BulkDeleteStopesMutation>(
    (mutation) => enqueueReticulationWrite(() => api.mineModels.removeStopes(mutation.projectId, mutation.form, activeScenarioId)),
    {
      onSuccess: (data) => {
        const removedCount = data?.removedStopeIds?.length ?? 0;
        const skipped = data?.skipped ?? [];

        if (removedCount > 0) {
          enqueueSnackbar(
            `${removedCount} ${removedCount === 1 ? i18n._('stope successfully deleted') : i18n._('stopes successfully deleted')}`,
            { variant: 'success' },
          );
        }

        skipped.forEach((entry) => {
          enqueueSnackbar(
            `${i18n._('Skipped stope')} ${entry.stopeId ?? ''}: ${entry.reason ?? i18n._('Unknown reason')}`,
            { variant: 'warning' },
          );
        });

        if (removedCount === 0 && skipped.length === 0) {
          enqueueSnackbar(i18n._('No stopes were removed.'), { variant: 'info' });
        }

        queryClient.invalidateQueries(MINE_MODEL_KEY, { exact: false });
        queryClient.invalidateQueries(MINE_MODEL_SUMMARY_KEY, { exact: false });
        queryClient.invalidateQueries(RETICULATION_DATA_EXPORT_KEY, { exact: false });
        queryClient.invalidateQueries(STOPE_BACKFILLS_KEY, { exact: false });
      },
      onError: () => {
        enqueueSnackbar(i18n._('Failed to delete stopes.'), { variant: 'error' });
      },
    },
  );
};

export const useMarkStopeAsCompleteMutation = makeUpdateMutation<void, string>(
  MINE_MODEL_KEY, null,
  (api, projectId) => (stopeId) => api.stopes.markComplete(projectId, stopeId),
  () => i18n._('Stope marked as complete'),
  [RETICULATION_DATA_EXPORT_KEY, STOPE_BACKFILLS_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useMarkStopeAsIncompleteMutation = makeUpdateMutation<void, string>(
  MINE_MODEL_KEY, null,
  (api, projectId) => (stopeId) => api.stopes.markIncomplete(projectId, stopeId),
  () => i18n._('Stope marked as incomplete'),
  [RETICULATION_DATA_EXPORT_KEY, STOPE_BACKFILLS_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface UpdateStopeGeometryFileMutation {
  stopeId: string;
  file?: FileParameter;
  entityGuid?: string;
}

export const useUpdateStopeGeometryFile = makeUpdateMutation<void, UpdateStopeGeometryFileMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.updateStopeGeometryFile(
    projectId,
    mutation.stopeId,
    mutation.entityGuid,
    scenarioId,
    mutation.file,
  )),
  () => i18n._('Geometry added to stope'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface UpdateTunnelProfileMutation {
  file?: FileParameter;
  entityGuid?: string;
}

// Import / replace the single mine-wide tunnel profile. Returns the persisted profile.
// No query invalidation: the caller rebuilds the in-memory profile from the returned DTO,
// avoiding a full model reload.
export const useUpdateTunnelProfile = makeUpdateMutation<TunnelProfileDto, UpdateTunnelProfileMutation>(
  MINE_MODEL_KEY, null,
  (api, projectId) => (mutation) => api.mineModels.updateTunnelProfile(projectId, mutation.entityGuid, mutation.file),
  () => '',
  [],
  false,
);

export interface UpdateTunnelProfileSettingsMutation {
  visible: boolean;
  opacity: number;
}

// Persist show/hide + transparency. Fire-and-forget: local model state is the live source of
// truth during the session, so we do not invalidate or reload the model on success.
export const useUpdateTunnelProfileSettings = makeUpdateMutation<void, UpdateTunnelProfileSettingsMutation>(
  MINE_MODEL_KEY, null,
  (api, projectId) => (mutation) => api.mineModels.updateTunnelProfileSettings(
    projectId,
    { visible: mutation.visible, opacity: mutation.opacity } as TunnelProfileSettingsForm,
  ),
  () => '',
  [],
  false,
);

export const useRemoveTunnelProfile = makeUpdateMutation<void, void>(
  MINE_MODEL_KEY, null,
  (api, projectId) => () => api.mineModels.removeTunnelProfile(projectId),
  () => i18n._('Tunnel profile removed'),
  [],
  false,
);

export interface ParseDufForBulkImportMutation {
  fileParam: FileParameter;
}

export const useParseDufForBulkImport = makeUpdateMutation<DufBulkImportParseResponse, ParseDufForBulkImportMutation>(
  MINE_MODEL_KEY, null,
  (api, projectId) => (mutation) => api.mineModels.parseDufForBulkImport(projectId, mutation.fileParam),
  () => '',
  [],
);

export interface BulkImportStopesMutation {
  form: BulkImportStopesForm;
}

export const useBulkImportStopes = makeUpdateMutation<BulkImportStopesResultDto, BulkImportStopesMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => api.mineModels.bulkImportStopes(projectId, mutation.form, scenarioId)),
  () => i18n._('Solids imported successfully'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface BulkUpdateStopeGeometryMutation {
  form: BulkUpdateStopeGeometryForm;
}

// The server refuses request bodies over 30,000,000 bytes, and JSON-escaping the OBJ line breaks adds a few percent.
const MAX_GEOMETRY_BATCH_CHARS = 20000000;

export const useBulkUpdateStopeGeometry = makeUpdateMutation<void, BulkUpdateStopeGeometryMutation>(
  MINE_MODEL_KEY, null,
  serialiseReticulationWrites((api, projectId, scenarioId) => (mutation) => batchBySize(
    mutation.form.entries ?? [],
    (entry) => entry.objContent?.length ?? 0,
    MAX_GEOMETRY_BATCH_CHARS,
  ).reduce<Promise<void>>(
    (previous, entries) => previous.then(() => api.mineModels.bulkUpdateStopeGeometry(projectId, { entries }, scenarioId)),
    Promise.resolve(),
  )),
  () => i18n._('Geometry uploaded'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface InspectionPointMutation {
  projectId: string;
  spoolId: string;
  name: string;
}

export const useAddInspectionPoint = makeCreateMutation<string, InspectionPointMutation>(
  INSPECTION_POINTS_KEYS,
  (api) => (mutation) => api.mineModels.addInspectionPoint(mutation.projectId, mutation.spoolId, mutation.name),
  () => i18n._('A new inspection point has been created'),
);

export interface AssignInspectionPointSpoolMutation {
  inspectionPointId: string;
  spoolId: string;
}

export const useAssignInspectionPointSpoolMutation = makeUpdateMutation<void, AssignInspectionPointSpoolMutation>(
  INSPECTION_POINTS_KEY, INSPECTION_POINTS_KEYS,
  (api, projectId) => (mutation) => api.mineModels.assignInspectionPointSpool(projectId, mutation.inspectionPointId, mutation.spoolId),
  () => i18n._('Inspection point assigned to spool'),
  [INSPECTION_POINTS_KEY, INSPECTION_POINTS_KEYS],
);

export interface GetInspectionPointArgs {
  projectId: string;
  inspectionPointId: string;
}

export const useGetInspectionPointQuery = makeGetQuery<InspectionPointDto, GetInspectionPointArgs>(
  INSPECTION_POINTS_KEY,
  (api, request) => () => api.mineModels.getInspectionPoint(request.projectId, request.inspectionPointId),
);

export interface SearchInspectionPointsArgs {
  projectId: string;
  page: number;
  pageSize: number;
  name?: string;
}

export const useSearchInspectionPointsQuery = makeListQuery<SearchInspectionPointsArgs, PagedListOfInspectionPointDto>(
  INSPECTION_POINTS_KEYS,
  (api, params) => () => api.mineModels.searchInspectionPoint(params.projectId, params.page, params.pageSize),
);

export const useGetInspectionPointsByMineModelIdQuery = makeListQuery<string, InspectionPointDto[]>(
  INSPECTION_POINTS_KEYS,
  (api, mineModelId) => () => api.mineModels.getInspectionPointsByMineModelId(mineModelId),
);

export interface WearDataMutation {
  mineModelId: string;
  form: WearDataForm;
}

export const useAddWearData = makeCreateMutation<string, WearDataMutation>(
  WEAR_DATA_LIST_KEY,
  (api) => (mutation) => api.mineModels.addWearData(mutation.mineModelId, mutation.form),
  () => i18n._('Wear data successfully added.'),
);

export interface DeleteMutation {
  projectId: string;
  entityId: string;
}

export const useDeleteInspectionPointMutation = makeDeleteMutation<void, DeleteMutation>(
  INSPECTION_POINTS_KEY, INSPECTION_POINTS_KEYS,
  (api) => (mutation) => api.mineModels.removeInspectionPoint(mutation.projectId, mutation.entityId),
  () => i18n._('Inspection point successfully deleted'),
  (mutation) => mutation.projectId,
);

export const useDeleteWearDataMutation = makeDeleteMutation<void, DeleteMutation>(
  WEAR_DATA_KEY, WEAR_DATA_LIST_KEY,
  (api) => (mutation) => api.mineModels.removeWearData(mutation.projectId, mutation.entityId),
  () => i18n._('Wear dataset successfully deleted'),
  (mutation) => mutation.projectId,
);

export interface SearchWearDataArgs {
  mineModelId: string;
  page: number;
  pageSize: number;
  inspectionPoint: string;
}

export const useSearchWearDataQuery = makeListQuery<SearchWearDataArgs, PagedListOfWearDataDto>(
  WEAR_DATA_LIST_KEY,
  (api, { mineModelId, inspectionPoint, page, pageSize }) => () => api.mineModels.searchWearData(mineModelId, page, pageSize, inspectionPoint),
);

export interface UpdateWearDataArgs {
  wearDataId: string;
  form: UpdateWearDataForm;
}

export const useUpdateWearDataMutation = makeUpdateMutation<void, UpdateWearDataArgs>(
  WEAR_DATA_KEY, WEAR_DATA_LIST_KEY,
  (api, projectId) => (mutation) => api.mineModels.updateWearData(projectId, mutation.wearDataId, mutation.form),
  () => i18n._('Wear data updated successfully'),
  [WEAR_DATA_KEY],
);

export interface WearDataQuery {
  projectId: string;
  wearDataId: string;
}

export const useWearDataQuery = makeGetQuery<WearDataDto, WearDataQuery>(
  WEAR_DATA_KEY,
  (api, params) => () => api.mineModels.getWearData(params.projectId, params.wearDataId),
);

// Throughput records carrying a series for one spool
export interface NodeVolumeRecordsBySpoolIdQuery {
  mineModelId: string;
  spoolId: string;
  startDate?: Date;
  endDate?: Date;
}

export const useNodeVolumeRecordsBySpoolIdQuery = makeListQuery<NodeVolumeRecordsBySpoolIdQuery, NodeVolumeRecordDto[]>(
  NODE_VOLUME_RECORD_KEY,
  (api, params) => () => api.mineModels.getNodeVolumeRecordsBySpoolId(
    params.mineModelId,
    params.spoolId,
    params.startDate?.toISOString(),
    params.endDate?.toISOString(),
  ),
);

export interface ImportDufFileMutation {
  fileParam: FileParameter;
}

export interface ImportDufResponse {
  imperialUnits: boolean;
  entities: Record<string, DufEntityDto[]>;
}

export interface DufVertexDto {
  x: number;
  y: number;
  z: number;
  attributes?: Record<string, string> | null;
}

export interface DufEntityDto {
  guid: string;
  type: string;
  name?: string | null;
  vertexList?: DufVertexDto[];
  xProperties?: Record<string, string> | null;
  vertexAttributeNames?: string[] | null;
}

export const useImportDufFile = makeUpdateMutation<FileResponse, ImportDufFileMutation>(
  MINE_MODEL_KEY,
  null,
  (api, projectId) => (mutation) => api.mineModels.importDUF(projectId, mutation.fileParam),
  () => '',
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface DufImportMutation {
  projectId: string;
  projectName: string | null;
  nodeId: string;
  data: DxfImportDataForm[];
}

export const useDufImportMutation = makeCreateMutation<string, DufImportMutation>(
  MINE_MODEL_KEY,
  serialiseReticulationWrites((api, scenarioId) => (mutation) => api.mineModels.dufImport(mutation.projectId ?? '', mutation.data, scenarioId)),
  () => i18n._('Node data updated'),
  [RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface SearchNodeVolumeRecordsQuery {
  mineModelId: string;
  page?: number;
  pageSize?: number;
  startDate?: Date;
  endDate?: Date;
}

export const useSearchNodeVolumeRecordsQuery = makeListQuery<SearchNodeVolumeRecordsQuery, PagedListOfT<NodeVolumeRecordDto>>(
  NODE_VOLUME_RECORD_KEY,
  (api, params) => () => api.mineModels.searchNodeVolumeRecord(
    params.mineModelId,
    params.page,
    params.pageSize,
    params.startDate?.toISOString(),
    params.endDate?.toISOString(),
  ),
);

// Entity Groups
export const ENTITY_GROUPS_KEY = 'entity-groups';

export const useGetModelEntityGroupsQuery = makeGetQuery<ModelEntityGroupDto[], string>(
  ENTITY_GROUPS_KEY,
  (api, mineModelId) => () => api.mineModels.getEntityGroups(mineModelId),
);

export interface CreateEntityGroupMutation {
  mineModelId: string;
  form: CreateModelEntityGroupForm;
}

export const useCreateModelEntityGroupMutation = makeCreateMutation<string, CreateEntityGroupMutation>(
  ENTITY_GROUPS_KEY,
  (api) => (mutation) => api.mineModels.createEntityGroup(mutation.mineModelId, mutation.form),
  () => i18n._('Group created'),
  [ENTITY_GROUPS_KEY],
);

export interface UpdateEntityGroupMutation {
  mineModelId: string;
  groupId: string;
  form: UpdateModelEntityGroupForm;
}

export const useUpdateModelEntityGroupMutation = makeUpdateMutation<void, UpdateEntityGroupMutation>(
  ENTITY_GROUPS_KEY, null,
  (api) => (mutation) => api.mineModels.updateEntityGroup(mutation.mineModelId, mutation.groupId, mutation.form),
  () => i18n._('Group updated'),
  [ENTITY_GROUPS_KEY],
);

export interface DeleteEntityGroupMutation {
  mineModelId: string;
  groupId: string;
}

export const useDeleteModelEntityGroupMutation = makeDeleteMutation<void, DeleteEntityGroupMutation>(
  ENTITY_GROUPS_KEY, null,
  (api) => (mutation) => api.mineModels.deleteEntityGroup(mutation.mineModelId, mutation.groupId),
  () => i18n._('Group deleted'),
  (mutation) => mutation.mineModelId,
  [ENTITY_GROUPS_KEY],
);
